import React, { useContext, useState } from "react"
import classnames from "classnames"
import { flexRender, Header, Table, RowModel } from "@tanstack/react-table"

import { GenericObject } from "../../types"

import { GlobalProps } from "../../utilities/globalProps"

import Flex from "../../pb_flex/_flex"
import Checkbox from "../../pb_checkbox/_checkbox"
import SectionSeparator from "../../pb_section_separator/_section_separator"
import PbReactPopover from "../../pb_popover/_popover";
import Icon from "../../pb_icon/_icon"

import { SortIconButton } from "./SortIconButton"
import { ToggleIconButton } from "./ToggleIconButton"
import { displayIcon } from "../Utilities/IconHelpers"
import { findColumnDefByAccessor } from "../Utilities/ColumnStylingHelper"
import { playbookColumnLayoutStylesFromMeta } from "../Utilities/ColumnLayoutHelper"
import { updateExpandAndCollapseState } from "../Utilities/ExpansionControlHelpers"

import { isChrome } from "../Utilities/BrowserCheck"

import AdvancedTableContext from "../Context/AdvancedTableContext"

type TableHeaderCellProps = {
  enableSorting?: boolean
  enableToggleExpansion?: "all" | "header" | "none"
  handleExpandOrCollapse?: () => void
  header?: Header<GenericObject, unknown>
  headerChildren?: React.ReactNode | React.ReactNode[]
  isPinnedLeft?: boolean
  loading?: boolean
  persistToggleExpansionButton?: boolean
  sortIcon?: string | string[]
  table?: Table<GenericObject>
} & GlobalProps

export const TableHeaderCell = ({
  enableSorting,
  enableToggleExpansion,
  handleExpandOrCollapse,
  header,
  headerChildren,
  isPinnedLeft = false,
  loading,
  sortIcon,
  table
}: TableHeaderCellProps) => {
  const {
    columnDefinitions,
    expanded,
    setExpanded,
    expandByDepth,
    enableSortingRemoval,
    onExpandByDepthClick,
    toggleExpansionIcon,
    sortControl,
    responsive,
    selectableRows,
    hasAnySubRows,
    showActionsBar,
    persistToggleExpansionButton,
    stickyLeftColumn,
    inlineRowLoading,
    isActionBarVisible,
    cascadeCollapse,
  } = useContext(AdvancedTableContext);

  type justifyTypes = "none" | "center" | "start" | "end" | "between" | "around" | "evenly"
  
  
  const toggleSortButton = (event: React.SyntheticEvent) => {
    if (sortControl) {
      const sortIsDesc = header?.column.getIsSorted() === "desc"
      sortIsDesc
        ? sortControl.onChange({ desc: true })
        : sortControl.onChange({ desc: false })
    } else {
      header?.column.getToggleSortingHandler()(event)
    }
  }
  const alignmentMap: Record<string, "start" | "center" | "end"> = {
    left: "start",
    center: "center",
    right: "end",
  };

 // Look up the “owning” columnDefinition by accessor. Needed for multi column logic
 const colDef = header
   ? findColumnDefByAccessor(columnDefinitions, header.column.id)
   : undefined

 const headerAlignment =   colDef?.columnStyling?.headerAlignment ?? colDef?.columnStyling?.headerAligment
 const headerBackgroundColor = colDef?.columnStyling?.headerBackgroundColor
 const headerFontColor = colDef?.columnStyling?.headerFontColor

  const isLeafColumn =
  header?.column.getLeafColumns().length === 1 &&
  header?.column.getLeafColumns()[0].id === header.column.id

  const columnHasVisibleLeaf = (col: any): boolean =>
    col.getIsVisible?.() ||
    (Array.isArray(col.columns) &&
      col.columns.some((child: any) => columnHasVisibleLeaf(child)));

   // Check on column position in stack + visibility to add the vertical border
  const isLastHeaderCell = (() => {
    if (!header) return false;

    if (header.colSpan > 1 && header.column.parent !== undefined) return true;
    const parent = header.column.parent;

    if (!parent) {
      const headerGroups = table?.getHeaderGroups() ?? [];
      const isSubheader = (header.headerGroup?.depth ?? 0) > 0;
      if (isSubheader && headerGroups.length > 1 && header.index !== 0) return true;

      const topHeaders = headerGroups[0]?.headers.filter((item: any) => columnHasVisibleLeaf(item.column));
      return topHeaders?.at(-1)?.id === header.id;
    }
    const visibleSiblings = parent.columns.filter(columnHasVisibleLeaf);
    return visibleSiblings.at(-1) === header.column;
  })();

const cellClassName = classnames(
  "table-header-cells",
  `${showActionsBar && isActionBarVisible && "header-cells-with-actions"}`,
  `${isChrome() ? "chrome-styles" : ""}`,
  `${enableSorting ? "table-header-cells-active" : ""}`,
  { "pinned-left": responsive === "scroll" && isPinnedLeft },
   isLastHeaderCell ? "last-header-cell" : "",
  stickyLeftColumn && stickyLeftColumn.length > 0 && isPinnedLeft ? 'sticky-left' : "",
  colDef?.columnStyling?.headerPadding && `p_${colDef?.columnStyling?.headerPadding}`
); 

const cellId = `${loading ? 
    `loading-${header?.id}`
    : `${header?.id}`
}`

const isToggleExpansionEnabledLoading =
  header?.index === 0 &&
  loading &&
  (enableToggleExpansion === "all" || "header") &&
  enableToggleExpansion !== "none"
  
const isToggleExpansionEnabled =
  header?.index === 0 &&
  !loading &&
  (enableToggleExpansion === "all" || "header") &&
  enableToggleExpansion !== "none"

  let justifyHeader: justifyTypes;

  if (headerAlignment && alignmentMap[headerAlignment]) {
    justifyHeader = alignmentMap[headerAlignment];
  } else if (
    (header?.index === 0 && hasAnySubRows) ||
    (header?.index === 0 && inlineRowLoading) ||
    (header?.index === 0 && isToggleExpansionEnabled)
  ) {
    justifyHeader = enableSorting ? "between" : "start";
  } else {
    justifyHeader = isLeafColumn ? "end" : "center";
  }
  
  const [showPopover, setShowPopover] = useState(false)

  const togglePopover = () => setShowPopover((prev) => !prev)
  const handleShouldClose = (shouldClose: boolean) =>
    setShowPopover(!shouldClose)

  const popoverReference = (
      <div className="gray-icon toggle-all-icon" 
          onClick={togglePopover}
      >
          <Icon icon={displayIcon(toggleExpansionIcon)[0]} />
      </div>
  )

  const handleExpandDepth = (depth: number) => {
    if (onExpandByDepthClick) {
      const flatRows = table?.getRowModel().flatRows
      onExpandByDepthClick(depth, flatRows)
    }
    const updated = updateExpandAndCollapseState(
      table.getRowModel(),
      expanded,
      undefined,
      depth,
      cascadeCollapse
    )
    setExpanded(updated)
  }
  

  return (
    <th
        align={headerAlignment ? headerAlignment : "right"}
        className={cellClassName}
        colSpan={header?.colSpan}
        id={cellId}
        key={`${header?.id}-header`}
        style={{
          ...playbookColumnLayoutStylesFromMeta(header?.column?.columnDef),
          backgroundColor: headerBackgroundColor,
          color: headerFontColor,
          left: isPinnedLeft
            ? header?.index === 1 //Accounting for set min-width for first column
              ? '180px'
              : `${header?.column.getStart("left")}px`
            : undefined,
        }}
    >
      {header?.isPlaceholder ? null : headerChildren && header?.index === 0 ? (
        <Flex alignItems="center">
          {headerChildren}
          <div>
            {flexRender(header.column.columnDef.header, header.getContext())}
          </div>
        </Flex>
      ) : (
        <Flex
            alignItems="center"
            justify={justifyHeader}
        >
          {
            selectableRows && header?.index === 0 && hasAnySubRows && (
              <Checkbox
                  checked={table?.getIsAllRowsSelected()}
                  indeterminate={table?.getIsSomeRowsSelected()}
                  onChange={table?.getToggleAllRowsSelectedHandler()}
              />
            )
          }
          {isToggleExpansionEnabled &&  ((hasAnySubRows) || (inlineRowLoading && persistToggleExpansionButton)) && !expandByDepth && (
              <ToggleIconButton onClick={handleExpandOrCollapse} />
            )}
          {isToggleExpansionEnabled && hasAnySubRows && expandByDepth && (

                    <PbReactPopover
                        closeOnClick="any"
                        placement="bottom-start"
                        reference={popoverReference}
                        shouldClosePopover={handleShouldClose}
                        show={showPopover}
                        zIndex={3}
                    > 
                    {expandByDepth.map((option:{ [key: string]: any }, index: number) => (
                      <>
                      <Flex
                          alignItems="center"
                          className="pb-advanced-table-popover-option"
                          cursor="pointer"
                          htmlOptions={{onClick: () => {handleExpandDepth(option.depth)} }}
                          paddingX="sm"
                          paddingY="xs"
                          >
                            {option.label}
                      </Flex>
                      {index !== expandByDepth.length - 1 && <SectionSeparator/>}
                      </>
                        ))}
                    </PbReactPopover>
                 

            )}

          {isToggleExpansionEnabledLoading &&(
              <div className="loading-toggle-icon header-toggle-icon" />
            )}

          <Flex
              className={`${header?.index === 0 && enableSorting && "header-sort-button pb_th_link"} ${header?.index !== 0 && header?.column.getCanSort() && "header-sort-secondary-columns"}`}
              cursor={((header?.index !== 0 && header?.column.getCanSort()) || (header?.index === 0 && enableSorting) ? "pointer" : "default")}
              {...(((header?.index !== 0 && header?.column.getCanSort()) || (header?.index === 0 && enableSorting)) && {
                  htmlOptions: {
                    onClick: (event: React.MouseEvent) => toggleSortButton(event),
                    onKeyDown: (event: React.KeyboardEvent) => {
                      if (event.key === "Enter") {
                        toggleSortButton(event)
                      }
                    },
                    tabIndex: 0,
                  },
                })}
              justify={header?.index === 0 && enableSorting ? "between" : headerAlignment ? alignmentMap[headerAlignment] : "none"}
              paddingLeft={header?.index === 0 ? enableSorting ? "xxs" : "xs" : "none"}
          >
            <div>
              {flexRender(header?.column.columnDef.header, header?.getContext())}
            </div>

            {((header?.index !== 0 && header?.column.getCanSort()) || (header?.index === 0 && enableSorting)) &&
              (loading ? (
                <div className="loading-toggle-icon" />
              ) : (
                <SortIconButton 
                    enableSortingRemoval={enableSortingRemoval}
                    header={header} 
                    sortIcon={sortIcon} 
                />
              ))}
          </Flex>
        </Flex>
      )}
    </th>
  )
}
